import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { ListTable } from 'gnos-tables'
import cx from 'classnames'
import _ from 'lodash'

import { createListTableId } from '../../../../../../helpers'

import config from '../../../../../../../../../config'

import { LOCALE_PREFIX, TABS_PRIORITY, GROUP_VAT_TABLE_KEY } from './constants'

import Tabs from '../../../../../../../../../components/Tabs'
import CustomDate from '../../../../../../../../../components/CustomDate'
import Entity from '../../../../../../../../../components/TableCell/components/Entity'
import NoData from '../../../../../../../components/Workspace/components/NoData'

import hocBlock from '../../../../../../../components/Workspace/components/HocBlock'
import hocConnect from './hocConnect'

import './style.scss'

@hocBlock(config.modules.report.key, { levelReload: true })
@hocConnect
@injectIntl
export default class RegistrationInformation extends Component {
    static propTypes = {
        activeTab: PropTypes.func.isRequired,
        block: PropTypes.string.isRequired,
        data: PropTypes.object,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object.isRequired,
        level: PropTypes.string.isRequired,
        listTableRemoveData: PropTypes.func.isRequired,
        removeTabsData: PropTypes.func.isRequired,
        transformedData: PropTypes.object,
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.level !== nextProps.level) return true
        if (this.props.id !== nextProps.id) return true
        if (!_.isEqual(this.props.data, nextProps.data)) return true
        if (!_.isEqual(this.props.transformedData, nextProps.transformedData)) return true

        return false
    }

    componentDidUpdate(prevProps) {
        const { id, block, level, listTableRemoveData, removeTabsData } = prevProps

        if (level !== this.props.level) {
            removeTabsData(`${config.modules.report.key}_${id}_${block}_tabs`)
            _.map(TABS_PRIORITY, (tabId) => {
                listTableRemoveData(createListTableId(config.modules.report.key, id, block, tabId))
            })
        }
    }

    handleGroupVatBtnClick = () => {
        const { activeTab, id, block } = this.props

        activeTab(`${config.modules.report.key}_${id}_${block}_tabs`, 1)
    }

    renderValueColumn = (value, type, key) => (
        <div className={`valueColumn ${type}`}>
            {value.subject && (
                <div className={'entity-value-wrap'}>
                    <Entity
                        entity={_.get(value, 'value', null)}
                        entId={value.subject.id}
                        value={value.subject.name}
                        inactive={type === 'history'}
                        type={value.subject.type}
                        risk={value.subject.indicators.risk}
                        caution={value.subject.indicators.caution}
                        notice={value.subject.indicators.notice}
                        showBurger
                    />
                </div>
            )}
            {!value.subject && (
                <div>
                    {value.value || null}
                    {key === GROUP_VAT_TABLE_KEY && (
                        <button className={'group-vat-btn'} onClick={this.handleGroupVatBtnClick}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.groupVatBtn`} />
                        </button>
                    )}
                </div>
            )}
        </div>
    )

    renderDateColumn = (value, type) => {
        const { intl } = this.props

        const fromDate = value.startDate ? (
            <CustomDate date={value.startDate.value} precision={value.startDate.precision} />
        ) : null
        const toDate = value.endDate ? (
            <CustomDate date={value.endDate.value} precision={value.endDate.precision} />
        ) : null

        return (
            <div className={`dateColumn ${type}`}>
                {fromDate && (
                    <div className={'date'}>
                        <span className={'text'}>
                            {intl.formatMessage({ id: `${LOCALE_PREFIX}.listTable.column.date.from` })}
                        </span>
                        <span className={'value'}>{fromDate}</span>
                    </div>
                )}
                {toDate && (
                    <div className={'date'}>
                        <span className={'text'}>
                            {intl.formatMessage({ id: `${LOCALE_PREFIX}.listTable.column.date.to` })}
                        </span>
                        <span className={'value'}>{toDate}</span>
                    </div>
                )}
            </div>
        )
    }

    renderTabsSharedContent = ({ tabId }) => {
        const { block, id, data, intl } = this.props

        const tabData = _.get(data, tabId, {})

        return (
            <div className={'tabs-content'}>
                <ListTable
                    id={createListTableId(config.modules.report.key, id, block, tabId)}
                    staticData={tabData}
                    columnsDefinition={{
                        title: {
                            width: 350,
                            Cell: (value) => (
                                <div className={cx('title', _.get(value, 'isHistorical', false) && 'historical')}>
                                    {_.get(value, 'text', null)}
                                </div>
                            ),
                            ExpandCell: ({ isNested, text, tables }) => {
                                if (isNested && _.size(tables) === 1) return <div className={'title'}>{text}</div>
                                return null
                            },
                        },
                        top: {
                            visibleRows: 10,
                            stepRows: 10,
                            columns: [
                                {
                                    accessor: 'item',
                                    Cell: ({ value, key }) => this.renderValueColumn(value, 'active', key),
                                },
                                {
                                    width: 300,
                                    accessor: 'dateRange',
                                    Cell: ({ value, key }) => this.renderDateColumn(value, 'active', key),
                                },
                            ],
                        },
                        nested: {
                            visibleRows: (value) => {
                                if (_.get(value, 'tables[0].nested', false)) {
                                    // If there is only one table and it's historical
                                    const tablesRowsLength = _.get(value, 'tables[0].rows', []).length

                                    if (tablesRowsLength > 10) return 10 // Only show 10 rows on first step

                                    return tablesRowsLength // If there is less than 10 rows, show all rows
                                }

                                return 0 // Hide table if there is multiple tables and the first one isn't historical
                            },
                            stepRows: 10,
                            expandText: (value) => {
                                if (
                                    !_.isNil(_.get(value, 'rows[0].item.subject')) &&
                                    !_.get(value, 'isHistorical', false)
                                )
                                    return intl.formatMessage({ id: `${LOCALE_PREFIX}.groupMembers` })

                                if (_.get(value, 'tables[0].isHistorical', false))
                                    return intl.formatMessage({ id: 'global.controlExpand' })

                                return intl.formatMessage({ id: 'global.history' })
                            },
                            columns: [
                                {
                                    accessor: 'item',
                                    Cell: ({ value, key, isHistorical }) =>
                                        this.renderValueColumn(value, isHistorical ? 'history' : 'active', key),
                                },
                                {
                                    width: 300,
                                    accessor: 'dateRange',
                                    Cell: ({ value, key, isHistorical }) =>
                                        this.renderDateColumn(value, isHistorical ? 'history' : 'active', key),
                                },
                            ],
                        },
                    }}
                    contextMenu={this.renderContextMenu}
                    texts={{
                        loading: intl.formatMessage({ id: 'global.loading' }),
                        noData: intl.formatMessage({ id: 'global.noData' }),
                        controlExpand: intl.formatMessage({ id: 'global.controlExpand' }),
                        controlMore: intl.formatMessage({ id: 'global.controlMore' }),
                        controlLess: intl.formatMessage({ id: 'global.controlLess' }),
                        controlAll: intl.formatMessage({ id: 'global.controlAll' }),
                        controlClose: intl.formatMessage({ id: 'global.controlClose' }),
                        controlFirst: intl.formatMessage({ id: 'global.controlFirst' }),
                    }}
                />
            </div>
        )
    }

    render() {
        const { block, id, data, transformedData, intl, level } = this.props

        if (!data || !transformedData) return <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.noData` })} />

        return (
            <div className={cx('block-registration-information')}>
                <Tabs
                    level={level}
                    id={`${config.modules.report.key}_${id}_${block}_tabs`}
                    defaultIndex={transformedData.defaultIndex}
                    sharedContent={this.renderTabsSharedContent}
                    tabs={_.map(TABS_PRIORITY, (tabId) => ({
                        tabId,
                        title: intl.formatMessage({ id: `${LOCALE_PREFIX}.${tabId}.title` }),
                        disabled: _.get(transformedData, tabId, true),
                    }))}
                    inline
                />
            </div>
        )
    }
}
